import React, { useState, useContext, useEffect } from 'react';
import CSS from 'csstype';
import '../../App.css';
import '../../tools/search-results/search-results.css';
import { withTranslation, WithTranslation } from 'react-i18next';
import { useAppSelector, useAppDispatch } from '../../app/hooks';

// images
import infoIcon from '../../icons/info.512.png';
import scissorsIcon from '../icons/scissors.512.png';
import gearsIcon from '../../icons/gears.gif';
import downloadIcon from '../../icons/action-download.512.png';
import dataStagingIcon from '../../icons/action-data-staging.512.png';
import dataStagingIconDisabled from '../../icons/action-data-staging-bw.512.png';
import launchJupyterIcon from '../../icons/action-launch-jupyter.512.png';
import launchJupyterIconDisabled from '../../icons/action-launch-jupyter-bw.512.png';
import launchCanfarIcon from '../../icons/action-launch-canfar.512.png';
import launchCanfarIconDisabled from '../../icons/action-launch-canfar-bw.512.png';
import viewAladinIcon from '../../icons/view-aladin.512.png';
import checkboxTicked from '../../icons/check-box-ticked.620.png';
import checkboxUnticked from '../../icons/check-box-unticked.620.png';

// types
import { FileBrowserPage } from '../../utils/types';

// types specifically for this component.
import { SelectedRowsType } from '../search-catalog/search-catalog-types';

// import state definition and functions (redux).
import {
  setPageDisplayed,
  setFilenameFilter,
  setNamespace,
} from '../data-management/file-browser-slice';

// functions
import {
  APIPrefix,
  extractFileFromAccessUrl,
  extractFileFromObsPublisherDid,
} from '../../utils/functions';

// classes
import ToolButton from '../../tools/tool-button';
import {
  Event,
  EventType,
  EventTarget,
  InitiateDMSearchParams,
  ExecuteADQLQuery,
} from '../../utils/events';

//	--------------------------------------------------------------------------
//
//	T Y P E S
//
//	--------------------------------------------------------------------------

// types specifically for this component.
import { QueryParameters } from '../search-catalog/search-catalog-types';
import { Table } from 'react-bootstrap';

//	--------------------------------------------------------------------------
//
//	R E D U X   S T A T E   M A N A G E M E N T
//
//	--------------------------------------------------------------------------

// import state definition and functions.
import {
  rowCheckedHandler,
  setHighlightedID,
  setSelectAll,
} from './search-catalog-caom-slice';
import { checkSelectAll, getFieldValue } from './search-catalog-caom-actions';
import { handleEvent } from '../../app-actions';

//	--------------------------------------------------------------------------
//
//	P R O P E R T I E S
//
//	--------------------------------------------------------------------------

//	--------------------------------------------------------------------------
//
//	H T M L   C U S T O M   C O M P O N E N T S
//
//	--------------------------------------------------------------------------

//	--------------------------------------------------------------------------
//
//	C L A S S   D E F I N I T I O N
//
//	--------------------------------------------------------------------------

export default function SearchResultsTable(props: {
  key: string;
  tabID: string;
  launchService: any;
}) {
  //
  //	--------------------------------------------------------------------------
  //
  //	R E D U X   S T A T E   H A N D L I N G
  //
  //	--------------------------------------------------------------------------

  const dispatch = useAppDispatch();

  // global state variables.
  const sG_SessionValid: boolean | undefined = useAppSelector(
    (state) => state.global.sessionValid,
  );
  const sG_DataManagementTokenObtained: boolean | undefined = useAppSelector(
    (state) => state.global.dataManagementTokenObtained,
  );
  const sG_DarkMode: boolean = useAppSelector((state) => state.global.darkMode);

  // LOCAL STATE VARIABLES:

  // footprint is highlighted.
  const sHighlightedID: string = useAppSelector(
    (state) => state.searchCatalogCAOM.highlightedID,
  );

  // maintain the state for the selected rows on the results tabs.
  const sSelectedRows: SelectedRowsType[] = useAppSelector(
    (state) => state.searchCatalogCAOM.selectedItems.selectedRows,
  );

  // footprint has been selected.
  const sSelectedIDs: { id: string; ra: number; dec: number }[] = useAppSelector(
    (state) => state.searchCatalogCAOM.selectedIDs,
  );

  // LOCAL STATE VARIABLES FOR ALL TABS:
  // ------------------------------

  // the retrieved data from the TAP server.
  const sTapDataAllTabs: { id: string; tapData: any[] }[] = useAppSelector(
    (state) => state.searchCatalogCAOM.tapData,
  );
  const sLoadingTapDataAllTabs: { id: string; loadingTapData: boolean }[] =
    useAppSelector((state) => state.searchCatalogCAOM.loadingTapData);
  const sColumnsAllTabs: { id: string; columns: string[] }[] = useAppSelector(
    (state) => state.searchCatalogCAOM.columns,
  );
  const sErrorMessageAllTabs: { id: string; errorMessage: string }[] = useAppSelector(
    (state) => state.searchCatalogCAOM.errorMessage,
  );
  const sSelectAllAllTabs: { id: string; selectAll: boolean }[] = useAppSelector(
    (state) => state.searchCatalogCAOM.selectedItems.selectAll,
  );

  // page handling.
  const sPageAllTabs: { id: string; page: number }[] = useAppSelector(
    (state) => state.searchCatalogCAOM.page,
  );
  const sPageSizeAllTabs: { id: string; pageSize: number }[] = useAppSelector(
    (state) => state.searchCatalogCAOM.pageSize,
  );

  // EXTRACT LOCAL STATE VARIABLES:
  // ------------------------------

  // the retrieved data from the TAP server.
  const sTapData: any[] =
    sTapDataAllTabs.find((item) => item.id === props.tabID)?.tapData ?? [];
  const sLoadingTapData: boolean =
    sLoadingTapDataAllTabs.find((item) => item.id === props.tabID)?.loadingTapData ??
    false;
  const sColumns: string[] =
    sColumnsAllTabs.find((item) => item.id === props.tabID)?.columns ?? [];
  const sErrorMessage: string =
    sErrorMessageAllTabs.find((item) => item.id === props.tabID)?.errorMessage ?? '';
  const sSelectAll: boolean =
    sSelectAllAllTabs.find((item) => item.id === props.tabID)?.selectAll ?? false;

  // page handling.
  const sPage: number = sPageAllTabs.find((item) => item.id === props.tabID)?.page ?? 0;
  const sPageSize: number =
    sPageSizeAllTabs.find((item) => item.id === props.tabID)?.pageSize ?? 10;

  //	--------------------------------------------------------------------------
  //
  //	D E C L A R A T I O N S
  //
  //	--------------------------------------------------------------------------

  // class-level constants.
  const TABLE_COL_GENERAL: CSS.Properties = {
    whiteSpace: 'nowrap',
    borderStyle: 'none solid none solid',
    borderWidth: '0px',
    borderColor: sG_DarkMode === true ? '#43444d' : '#bbbbbb',
    color: sG_DarkMode === true ? 'white' : 'black',
  };
  const TABLE_COL: CSS.Properties = {
    ...TABLE_COL_GENERAL,
    ...{
      background: 'transparent',
    },
  };
  const TABLE_COL_SELECTED: CSS.Properties = {
    ...TABLE_COL_GENERAL,
    ...{
      background: 'rgba( 255, 0, 0, 0.3 )',
    },
  };
  const TABLE_COL_HIGHLIGHTED: CSS.Properties = {
    ...TABLE_COL_GENERAL,
    ...{
      background: 'rgba( 0, 255, 0, 0.2 )',
    },
  };
  const TABLE_COL_HEADER: CSS.Properties = {
    border: 'none',
    position: 'sticky',
    zIndex: '1',
    backgroundColor: 'var(--window-background)',
    top: '0',
    alignItems: 'center',
    padding: '0px 10px 10px 10px',
  };
  const TABLE_COL_LAST: CSS.Properties = {
    borderStyle: 'none solid solid solid',
  };

  //	------------------------------------------------------------
  //
  //	Button handler for the select all checkbox.
  //
  //	------------------------------------------------------------

  const checkChange = (event: React.MouseEvent<HTMLButtonElement>) => {
    //
    // invert the select all check box.
    const selectAll = !sSelectAll;
    dispatch(setSelectAll({ id: props.tabID, selectAll: selectAll }));

    // get the list of selected rows from state.
    var rows: SelectedRowsType[] = [];
    var ticked: boolean[] = [];

    // loop over all the rows, adding them to the selected-items list..
    for (var i: number = 0; i < sTapData.length; i++) {
      //
      var newItem: SelectedRowsType = getValuesFromSourceList({ item: sTapData[i] });

      // extract namespace and name.
      var fileDetails: {
        namespace: string;
        filename: string;
      } = extractFileFromObsPublisherDid({ obsPublisherDid: newItem.obsPublisherDid });
      if (fileDetails.namespace === '' && newItem.accessUrl !== undefined) {
        fileDetails = extractFileFromAccessUrl({ accessUrl: newItem.accessUrl });
      }
      newItem.namespace = fileDetails.namespace;
      newItem.filename = fileDetails.filename;

      rows.push(newItem);
      ticked.push(selectAll === true);
      //
    }

    // update state.
    dispatch(rowCheckedHandler({ rows: rows, ticked: ticked }));
    //
  }; // checkChange

  //	------------------------------------------------------------
  //
  //	Copies a string value into the clipboard
  //
  //	------------------------------------------------------------

  const copyToClipboard = (str: string) => {
    const el = document.createElement('textarea');
    el.value = str;
    el.setAttribute('readonly', '');
    el.style.position = 'absolute';
    el.style.left = '-9999px';
    document.body.appendChild(el);
    const getSelection = document.getSelection();
    if (getSelection !== null) {
      const selected = getSelection.rangeCount > 0 ? getSelection.getRangeAt(0) : false;
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      if (selected) {
        getSelection.removeAllRanges();
        getSelection.addRange(selected);
      }
    }
  }; // copyToClipboard

  //	------------------------------------------------------------
  //
  //	Get some information and show this file in the data
  //	management tab.
  //
  //	------------------------------------------------------------

  function dataStaging(args: { row: number }) {
    //
    // get the access_url.
    const obsPublisherDid: string = getFieldValue({
      fieldName: 'obs_publisher_did',
      item: sTapData[args.row],
    });
    const accessUrl: string = getFieldValue({
      fieldName: 'access_url',
      item: sTapData[args.row],
    });

    // extract namespace and name.
    var fileDetails: {
      namespace: string;
      filename: string;
    } = extractFileFromObsPublisherDid({ obsPublisherDid: obsPublisherDid });
    if (fileDetails.namespace === '') {
      fileDetails = extractFileFromAccessUrl({ accessUrl: accessUrl });
    }

    // initiate a new data-management search.
    if (fileDetails.namespace !== '') {
      const filenames: string[] = ['', fileDetails.filename];
      dispatch(setFilenameFilter(filenames));
      const params: InitiateDMSearchParams = {
        namespace: fileDetails.namespace,
        filename: fileDetails.filename,
        pageDisplayed: FileBrowserPage.Items,
        showPage: true,
      };
      const newEvent: Event = {
        eventTarget: EventTarget.DATA_MANAGEMENT_TABLE,
        eventType: EventType.INITIATE_DM_SEARCH,
        parameters: params,
      };
      dispatch(handleEvent({ event: newEvent }));
    }
    //
  } // dataStaging

  //	------------------------------------------------------------
  //
  //	Get some information and download a file.
  //
  //	------------------------------------------------------------

  function downloadFile(args: { row: number }) {
    //
    // get the row that is to be retrieved.
    var row: any = sTapData[args.row];

    // get the obs_publisher_did and the access_url.
    const obsPublisherDid: string = getFieldValue({
      fieldName: 'obs_publisher_did',
      item: row,
    });
    const accessUrl: string = getFieldValue({
      fieldName: 'access_url',
      item: row,
    });

    // get the RA and Dec.
    var ra: string = '';
    var raColID: number = sColumns.findIndex((element) => element === 's_ra');
    if (raColID > -1) ra = row[sColumns[raColID]];
    var dec: string = '';
    var decColID: number = sColumns.findIndex((element) => element === 's_dec');
    if (decColID > -1) dec = row[sColumns[decColID]];

    // extract namespace and name.
    var fileDetails: {
      namespace: string;
      filename: string;
    } = extractFileFromObsPublisherDid({ obsPublisherDid: obsPublisherDid });
    if (fileDetails.namespace === '') {
      fileDetails = extractFileFromAccessUrl({ accessUrl: accessUrl });
    }

    // download the XML file.
    fetch(accessUrl)
      .then((response) => response.text())
      .then((responseText) => {
        // parse the XML.
        const parser = new DOMParser();
        var xml: any = parser.parseFromString(responseText, 'text/xml');

        // move to VOTABLE.
        const ivoID: string = '';
        var node: any = undefined;
        var idx: number = -1;
        [node, idx] = findChild({ node: xml, nodeName: 'VOTABLE' });

        // find the SODA-sync resource child.
        var sodaSyncNode: any = undefined;
        [sodaSyncNode, idx] = findChild({
          node: node,
          nodeName: 'RESOURCE',
          attributeName: 'ID',
          attributeValue: 'soda-sync',
        });

        // find the SODA-async resource child.
        var sodaAsyncNode: any = undefined;
        [sodaAsyncNode, idx] = findChild({
          node: node,
          nodeName: 'RESOURCE',
          attributeName: 'ID',
          attributeValue: 'soda-async',
        });

        // move to RESOURCE -> TABLE.
        [node, idx] = findChild({ node: node, nodeName: 'RESOURCE' });
        [node, idx] = findChild({ node: node, nodeName: 'TABLE' });

        // find the child node with a nodeName of FIELD and an attribute 'name' = 'ID'.
        var idNode: any = undefined;
        var idIndex: number = -1;
        [idNode, idIndex] = findChild({
          node: node,
          nodeName: 'FIELD',
          attributeName: 'name',
          attributeValue: 'ID',
        });

        // move to DATA -> TABLEDATA -> TR.
        [node, idx] = findChild({ node: node, nodeName: 'DATA' });
        [node, idx] = findChild({ node: node, nodeName: 'TABLEDATA' });
        [node, idx] = findChild({ node: node, nodeName: 'TR' });

        // find the nth item with node name "TD".
        [idNode, idx] = findChild({ node: node, nodeName: 'TD', nthItem: idIndex });
        var ivoLink: string = '';
        if (idNode !== undefined) ivoLink = idNode['textContent'];

        // extract the file name from the ID column (everything after the last /).
        var filename: string = '';
        if (ivoLink.lastIndexOf('/') > -1)
          filename = ivoLink.slice(ivoLink.lastIndexOf('/') + 1 - ivoLink.length);

        // extract the Rucio namespace from the ID column (everything after the ? and before the next /).
        var namespace: string = '';
        if (ivoLink.indexOf('?') > -1) {
          namespace = ivoLink.slice(ivoLink.indexOf('?') - ivoLink.length + 1);
          if (namespace.indexOf('/') > -1)
            namespace = namespace.slice(0, namespace.indexOf('/'));
          else namespace = '';
        }

        // Get the access url from the SODA-sync resource.
        var accessUrl: string = '';
        if (sodaSyncNode !== undefined) {
          var accessNode: any = undefined;
          [accessNode, idx] = findChild({
            node: sodaSyncNode,
            nodeName: 'PARAM',
            attributeName: 'name',
            attributeValue: 'accessURL',
          });
          if (accessNode !== undefined) {
            accessNode = findAttribute({ node: accessNode, attributeName: 'value' });
            if (accessNode !== undefined) accessUrl = accessNode['textContent'];
          }
        }

        // strip the domain from the url.
        var apiAddress: string = '';
        if (accessUrl.indexOf('.int/') > -1)
          apiAddress = accessUrl.slice(accessUrl.indexOf('.int/') + 4 - accessUrl.length);

        // exchange the data-management token for a download token.

        // fetch the file.
        if (
          accessUrl !== '' &&
          fileDetails.namespace !== '' &&
          filename !== '' &&
          ivoLink !== ''
        ) {
          // append the ivoLink to the access URL.
          accessUrl = accessUrl + '?ID=' + ivoLink;

          // append the circle to the access URL.
          if (ra !== '' && dec !== '')
            accessUrl = accessUrl + '&CIRCLE=' + ra + '%20' + dec + '%2060';
          copyToClipboard(accessUrl);

          /*const authorizationHeader: string = 'Authorization: Bearer ' + this.props.accessToken;
						fetch( apiAddress, { method: 'GET', headers: { 'Content-Type': 'application/fits' } } )
							.then
							(
								(response) =>
									response.blob()
									
							)
							.then
							(
								(blob) =>
								{
								
									// create blob link to download.
									const url = window.URL.createObjectURL( new Blob( [blob] ) );
									const link = document.createElement( 'a' );
									link.href = url;
									link.setAttribute( 'download', filename );

									// append to html link element page.
									document.body.appendChild( link );

									// start download.
									link.click();

									// clean up and remove the link.
									if (link.parentNode != null)
										link.parentNode.removeChild( link );
								}
							);*/
        }
      })
      .catch((error) => {
        console.log('error fetching the XML file: ', error);
      });
  } // downloadFile

  //	------------------------------------------------------------
  //
  //	Download a file from the LoTTS database.
  //
  //	------------------------------------------------------------
  /*	
	downloadFileLoTTS( args: { row: number } )
	{
	
		// get the row that is to be retrieved.
		var row: any = this.state.sourceList[ args.row ];
		
		// get the result (url).
		var result: string = "";
		var resultColID: number = this.state.columns.findIndex( element => element === 'result' )
		if (resultColID > -1)
			result = row[ this.state.columns[ resultColID ] ];
			
		// have we retrieved the result field?
		if (result != '')
		{
		
			// extract filename part (everything after the last '/'.
			var filename: string = '';
			if (result.lastIndexOf( '/' ) > -1)
				filename = result.slice( result.lastIndexOf( '/' ) + 1 - result.length );
			
			// strip the domain from the url.
			var apiAddress: string = '';
			if (result.indexOf( '.nl/' ) > -1)
				apiAddress = result.slice( result.indexOf( '.nl/' ) + 3 - result.length );
		
			fetch( apiAddress, { method: 'GET', headers: { 'Content-Type': 'application/fits' } } )
				.then
				(
					(response) =>
						response.blob()
				)
				.then
				(
					(blob) =>
					{
					
						// create blob link to download.
						const url = window.URL.createObjectURL( new Blob( [blob] ) );
						const link = document.createElement( 'a' );
						link.href = url;
						link.setAttribute( 'download', filename );

						// append to html link element page.
						document.body.appendChild( link );

						// start download.
						link.click();

						// clean up and remove the link.
						if (link.parentNode != null)
							link.parentNode.removeChild( link );
					}
				);
		
		}
	
	} // downloadFileLoTTS
*/
  //	------------------------------------------------------------
  //
  //	When given an XML node, look for an attribute with a given
  //	name matching a given value.
  //
  //	------------------------------------------------------------

  function findAttribute(args: {
    node: any;
    attributeName: string;
    attributeValue?: string;
  }) {
    // loop through the elements attached to this node to see if we can find one matching the required value.
    var returnNode: any = undefined;
    for (let i = 0; i < args.node['attributes'].length; i++) {
      var attribute: any = args.node['attributes'][i];
      if (attribute['nodeName'].toUpperCase() === args.attributeName.toUpperCase()) {
        var attributeValueOK: boolean = true;
        if (args.attributeValue !== undefined)
          attributeValueOK =
            attribute['nodeValue'].toUpperCase() === args.attributeValue.toUpperCase();
        if (attributeValueOK === true) returnNode = attribute;
      }
    }

    return returnNode;
  } // findAttribute

  //	------------------------------------------------------------
  //
  //	When given an XML node, look for a child with a given node name.
  //
  //	------------------------------------------------------------

  function findChild(args: {
    node: any;
    nodeName: string;
    attributeName?: string;
    attributeValue?: string;
    nthItem?: number;
  }) {
    var childIndex: number = -1;
    var elementCount: number = -1;
    var elementID: number = -1;

    if (args.node !== undefined)
      args.node['childNodes'].forEach((element: any, index: number) => {
        if (
          element['nodeName'].toUpperCase() === args.nodeName.toUpperCase() &&
          childIndex === -1
        ) {
          // look for the required attribute.
          var attributeMissing: boolean = false;
          if (
            args.attributeName !== undefined &&
            args.attributeValue !== undefined &&
            'attributes' in element
          ) {
            attributeMissing = true;
            const attribute = findAttribute({
              node: element,
              attributeName: args.attributeName,
              attributeValue: args.attributeValue,
            });
            if (attribute !== undefined) attributeMissing = false;
          }

          // increment the element count.
          if (attributeMissing === false) elementCount = elementCount + 1;

          // if we specifically need the nth item, then check if that's what we've found.
          var nthItemFound: boolean = true;
          if (args.nthItem !== undefined) nthItemFound = elementCount === args.nthItem;

          // are we happy ?
          if (attributeMissing === false && nthItemFound === true) {
            childIndex = index;
            elementID = elementCount;
          }
        }
      });
    if (childIndex > -1) return [args.node['childNodes'][childIndex], elementID];
    else return [undefined, -1];
  } // findChild

  //	------------------------------------------------------------
  //
  //	Populate the values we need for a selected-rows object.
  //
  //	------------------------------------------------------------

  function getValuesFromSourceList(args: { item: any }) {
    //
    var values: SelectedRowsType = {
      namespace: '',
      filename: '',
      obsPublisherDid: '',
      accessUrl: undefined,
      targetName: undefined,
      obsID: undefined,
      dataProductType: undefined,
      facilityName: undefined,
      sRA: undefined,
      sDec: undefined,
    };

    // get some values from the source list.
    if ('obs_publisher_did' in args.item) {
      values.obsPublisherDid = args.item['obs_publisher_did'];
    }
    if ('access_url' in args.item) {
      values.accessUrl = args.item['access_url'];
    }
    if ('target_name' in args.item) {
      values.targetName = args.item['target_name'];
    }
    if ('obs_id' in args.item) {
      values.obsID = args.item['obs_id'];
    }
    if ('dataproduct_type' in args.item) {
      values.dataProductType = args.item['dataproduct_type'];
    }
    if ('facility_name' in args.item) {
      values.facilityName = args.item['facility_name'];
    }
    if ('s_ra' in args.item) {
      if (
        typeof args.item['s_ra'] === 'number' &&
        args.item['s_ra'] === args.item['s_ra']
      ) {
        values.sRA = args.item['s_ra'];
      }
    }
    if ('s_dec' in args.item) {
      if (
        typeof args.item['s_dec'] === 'number' &&
        args.item['s_dec'] === args.item['s_dec']
      ) {
        values.sDec = args.item['s_dec'];
      }
    }

    // return something.
    return values;
    //
  } // getValuesFromSourceList

  //	------------------------------------------------------------
  //
  //	Launch a service on a site where this data is found.
  //
  //	------------------------------------------------------------

  function launchService(args: { type: string; row: number }) {
    //

    // get the obs_publisher_did and the access_url.
    const obsPublisherDid: string = getFieldValue({
      fieldName: 'obs_publisher_did',
      item: sTapData[args.row],
    });
    const accessUrl: string = getFieldValue({
      fieldName: 'access_url',
      item: sTapData[args.row],
    });

    // extract namespace and name.
    var fileDetails: {
      namespace: string;
      filename: string;
    } = extractFileFromObsPublisherDid({ obsPublisherDid: obsPublisherDid });
    if (fileDetails.namespace === '') {
      fileDetails = extractFileFromAccessUrl({ accessUrl: accessUrl });
    }

    // launch the notebook.
    if (fileDetails.namespace !== '' && fileDetails.filename !== '') {
      props.launchService({
        type: args.type,
        namespace: fileDetails.namespace,
        filename: fileDetails.filename,
      });
    }
    //
  } // launchService

  //	------------------------------------------------------------
  //
  //	Handler for onClick events (buttons).
  //
  //	------------------------------------------------------------

  const onClickHandlerBtn = (event: React.MouseEvent<HTMLButtonElement>) => {
    //
    const btn: HTMLButtonElement = event.currentTarget;

    // if we've clicked on a button to download data.
    if (btn.name.length > 16)
      if (btn.name.slice(0, 16) === 'btnDownloadData_') {
        // get the rest of the identifier, and convert to numeric.
        var rowID: string = btn.name.slice(16 - btn.name.length);
        let rowIDNumeric: number = -1;
        try {
          rowIDNumeric = Number(rowID);
        } catch (e) {}

        // download the file.
        if (rowIDNumeric > -1) {
          downloadFile({ row: rowIDNumeric });
        }
      }

    // if we've clicked on a button to download data.
    if (btn.name.length > 15)
      if (btn.name.slice(0, 15) === 'btnDataStaging_') {
        //
        // get the rest of the identifier, and convert to numeric.
        var rowID: string = btn.name.slice(15 - btn.name.length);
        let rowIDNumeric: number = -1;
        try {
          rowIDNumeric = Number(rowID);
        } catch (e) {}

        // download the file.
        if (rowIDNumeric > -1) {
          dataStaging({ row: rowIDNumeric });
        }
        //
      }

    // if we've clicked on a button to launch a notebook.
    if (btn.name.length > 11)
      if (btn.name.slice(0, 11) === 'btnJupyter_') {
        //
        // get the rest of the identifier, and convert to numeric.
        var rowID: string = btn.name.slice(11 - btn.name.length);
        let rowIDNumeric: number = -1;
        try {
          rowIDNumeric = Number(rowID);
        } catch (e) {}

        // download the file.
        if (rowIDNumeric > -1) {
          launchService({ type: 'JUPYTERHUB', row: rowIDNumeric });
        }
        //
      }

    // if we've clicked on a button to launch a notebook.
    if (btn.name.length > 10)
      if (btn.name.slice(0, 10) === 'btnCanfar_') {
        //
        // get the rest of the identifier, and convert to numeric.
        var rowID: string = btn.name.slice(10 - btn.name.length);
        let rowIDNumeric: number = -1;
        try {
          rowIDNumeric = Number(rowID);
        } catch (e) {}

        // download the file.
        if (rowIDNumeric > -1) {
          launchService({ type: 'CANFAR', row: rowIDNumeric });
        }
        //
      }
    //
  }; // onClickHandlerBtn

  //	------------------------------------------------------------
  //
  //	Handler for onClick events (images).
  //
  //	------------------------------------------------------------

  const onClickHandlerImg = (event: React.MouseEvent<HTMLImageElement>) => {
    const img: HTMLImageElement = event.currentTarget;

    // if we've clicked on a button to drill down further then update the drill-down table.
    if (img.id.length > 8)
      if (img.id.slice(0, 8) === 'btnSoda_') {
        // get the rest of the identifier, and convert to numeric.
        var rowID: string = img.id.slice(8 - img.id.length);
        let rowIDNumeric: number = -1;
        try {
          rowIDNumeric = Number(rowID);
        } catch (e) {}

        // download the file.
        downloadFile({ row: rowIDNumeric });
      }
  }; // onClickHandlerImg

  //	------------------------------------------------------------
  //
  //	Remove the highlight on a table row when the mouse leaves the row.
  //
  //	------------------------------------------------------------

  const onMouseLeave = (event: React.MouseEvent<HTMLTableRowElement>) => {
    dispatch(setHighlightedID(''));
  }; // onMouseLeave

  //	------------------------------------------------------------
  //
  //	Highlight a table row when the mouse moves over that row.
  //
  //	------------------------------------------------------------

  const onMouseOver = (event: React.MouseEvent<HTMLTableRowElement>, item: any) => {
    dispatch(setHighlightedID(item['obs_publisher_did']));
  }; // onMouseOver

  //	------------------------------------------------------------
  //
  //	Button handler for check box onChange events.
  //
  //	------------------------------------------------------------

  const rowCheckChange = (event: React.MouseEvent<HTMLButtonElement>, pRowID: number) => {
    //
    var newItem: SelectedRowsType = getValuesFromSourceList({
      item: sTapData[pRowID],
    });

    // extract namespace and name.
    var fileDetails: {
      namespace: string;
      filename: string;
    } = extractFileFromObsPublisherDid({ obsPublisherDid: newItem.obsPublisherDid });
    if (fileDetails.namespace === '' && newItem.accessUrl !== undefined) {
      fileDetails = extractFileFromAccessUrl({ accessUrl: newItem.accessUrl });
    }
    newItem.namespace = fileDetails.namespace;
    newItem.filename = fileDetails.filename;

    var pos: number = 0;
    if ('obsPublisherDid' in newItem) {
      pos = sSelectedRows.findIndex(
        (element) => element.obsPublisherDid === newItem.obsPublisherDid,
      );
    }
    const ticked: boolean = pos === -1;

    // raise an onChange event.
    dispatch(rowCheckedHandler({ rows: [newItem], ticked: [ticked] }));
    //
  }; // rowCheckChange

  //	------------------------------------------------------------
  //
  //	Check if the obs_publisher_did in this row is the selected
  //	one, and return the appropriate style.
  //
  //	------------------------------------------------------------

  function tableColStyle(args: {
    item: any;
    selectedIDs: { id: string; ra: number; dec: number }[];
    highlightedID: string;
  }) {
    var rowIsSelected: boolean = false;
    var rowIsHighlighted: boolean = false;
    if ('obs_publisher_did' in args.item) {
      if (sSelectedIDs.length > 0)
        for (var i = 0; i < sSelectedIDs.length; i++)
          if (args.item['obs_publisher_did'] === sSelectedIDs[i].id) rowIsSelected = true;
      if (
        args.item['obs_publisher_did'] === args.highlightedID &&
        args.highlightedID !== ''
      )
        rowIsHighlighted = true;
    }

    // return something.
    if (rowIsSelected === true) return TABLE_COL_SELECTED;
    else if (rowIsHighlighted === true) return TABLE_COL_HIGHLIGHTED;
    else return TABLE_COL;
  } // tableColStyle

  //	------------------------------------------------------------
  //
  //	Add a function to handle collection name clicks
  //
  //	------------------------------------------------------------

  function handleCollectionClick(collectionName: string) {
    //
    // Raise a new ADQL query
    const adqlQuery = `SELECT * FROM ivoa.ObsCore WHERE obs_collection = '${collectionName}'`;

    const params: ExecuteADQLQuery = {
      adqlQuery: adqlQuery,
    };

    const newEvent: Event = {
      eventTarget: EventTarget.SEARCH_CATALOG_FORM,
      eventType: EventType.EXECUTE_ADQL_QUERY,
      parameters: params,
    };

    dispatch(handleEvent({ event: newEvent }));
    //
  }

  //	------------------------------------------------------------
  //
  //	C O M P O N E N T S
  //
  //	------------------------------------------------------------

  //	------------------------------------------------------------
  //
  //	A HTML component that contains the action buttons for each
  //	row.
  //
  //	------------------------------------------------------------

  function Actions(args: { tableRow: object; index: number }) {
    //
    /*return	(
		
			<div>
				<img className="actions-image" src={infoIcon} alt="" width="22" height="22" />
				<img className="actions-image" id={"btnSoda_" + args.row.toString()} src={scissorsIcon} alt="" width="22" height="22"
						onClick={this.onClickHandlerImg} />
			</div>}
		
			)*/

    // check if this row contains a result instance.
    var result: string = '';
    if ('access_url' in args.tableRow)
      if (typeof args.tableRow['access_url'] === 'string')
        result = args.tableRow['access_url'];
    //if ('result' in args.tableRow)
    //	if (typeof args.tableRow[ 'result' ] === 'string')
    //		result = args.tableRow[ 'result' ];

    //    <button
    //      name={'btnDownloadData_' + args.index.toString()}
    //      type="button"
    //      title="Download data"
    //      className="action-button"
    //      onClick={onClickHandlerBtn}
    //      data-visible={result !== '' ? 'T' : 'F'}
    //    >
    //      <img src={downloadIcon} alt="" width="30" height="30" />
    //    </button>

    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          margin: '0px 0px 0px 0px',
        }}
      >
        <button
          name={'btnDataStaging_' + args.index.toString()}
          type="button"
          title="Data staging"
          disabled={sG_DataManagementTokenObtained === false}
          className="action-button"
          onClick={onClickHandlerBtn}
          data-visible={result !== '' ? 'T' : 'F'}
          style={{ height: '22px', background: 'transparent', border: 'transparent' }}
        >
          <img
            src={
              sG_DataManagementTokenObtained === true
                ? dataStagingIcon
                : dataStagingIconDisabled
            }
            role="decoration"
            alt=""
            width="20"
            height="20"
          />
        </button>
        <div style={{ flex: '0 0 3px' }} />
        <button
          name={'btnJupyter_' + args.index.toString()}
          type="button"
          title="Launch JupyterHUB"
          disabled={sG_DataManagementTokenObtained === false}
          className="action-button"
          onClick={onClickHandlerBtn}
          data-visible={result !== '' ? 'T' : 'F'}
          style={{ height: '22px', background: 'transparent', border: 'transparent' }}
        >
          <img
            src={
              sG_DataManagementTokenObtained === true
                ? launchJupyterIcon
                : launchJupyterIconDisabled
            }
            role="decoration"
            alt=""
            width="20"
            height="20"
          />
        </button>
        <div style={{ flex: '0 0 3px' }} />
        <button
          name={'btnCanfar_' + args.index.toString()}
          type="button"
          title="Launch CANFAR"
          disabled={sG_DataManagementTokenObtained === false}
          className="action-button"
          onClick={onClickHandlerBtn}
          data-visible={result !== '' ? 'T' : 'F'}
          style={{ height: '22px', background: 'transparent', border: 'transparent' }}
        >
          <img
            src={
              sG_DataManagementTokenObtained === true
                ? launchCanfarIcon
                : launchCanfarIconDisabled
            }
            role="decoration"
            alt=""
            width="20"
            height="20"
          />
        </button>
      </div>
    );
  } // Actions

  //	------------------------------------------------------------
  //
  //	Constructs a list of columns.
  //
  //	------------------------------------------------------------

  function TableColumns() {
    return sColumns.map((data: string) => {
      return <col key={data} />;
    });
  } // TableColumns

  //	------------------------------------------------------------
  //
  //	Constructs a table header row from a list of column names.
  //
  //	------------------------------------------------------------

  function TableHeadings() {
    return (
      <>
        {sColumns.map((column: string) => {
          return (
            <th
              style={{ ...TABLE_COL, ...TABLE_COL_HEADER }}
              data-hidden="true"
              key={column}
            >
              {column}
            </th>
          );
        })}
      </>
    );
  } // TableHeadings

  //	------------------------------------------------------------
  //
  //	A HTML component that takes a list of sources and renders
  //	them as a list of table rows.
  //
  //	------------------------------------------------------------

  function TableRows(args: {
    sourceList: any[];
    startIndex: number;
    onChange: any;
    selectedRows: SelectedRowsType[];
  }) {
    try {
      return (
        <>
          {args.sourceList.map((item, index) => (
            <tr
              className="search-results-table-row"
              style={{ border: 'none' }}
              onMouseOver={(event) => onMouseOver(event, item)}
              onMouseLeave={onMouseLeave}
            >
              <td
                style={{
                  ...tableColStyle({
                    item: item,
                    selectedIDs: sSelectedIDs,
                    highlightedID: sHighlightedID,
                  }),
                  ...(index === args.sourceList.length - 1 ? TABLE_COL_LAST : {}),
                }}
              >
                <button
                  type="button"
                  title="Select/deselect row"
                  className="action-button"
                  onClick={(event) =>
                    rowCheckChange(/* event = */ event, /* pRowID = */ index)
                  }
                  style={{
                    height: '22px',
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    background: 'transparent',
                    border: 'transparent',
                  }}
                >
                  <img
                    src={
                      args.selectedRows.findIndex(
                        (element) =>
                          element.obsPublisherDid ===
                          getFieldValue({ fieldName: 'obs_publisher_did', item: item }),
                      ) > -1
                        ? checkboxTicked
                        : checkboxUnticked
                    }
                    role="decoration"
                    alt=""
                    width="20"
                  />
                </button>
              </td>
              <td
                style={{
                  ...tableColStyle({
                    item: item,
                    selectedIDs: sSelectedIDs,
                    highlightedID: sHighlightedID,
                  }),
                  ...(index === args.sourceList.length - 1 ? TABLE_COL_LAST : {}),
                }}
              >
                {Actions({ tableRow: item, index: index })}
              </td>
              {sColumns.map((column) => (
                <td
                  className="text-colour"
                  style={{
                    ...tableColStyle({
                      item: item,
                      selectedIDs: sSelectedIDs,
                      highlightedID: sHighlightedID,
                    }),
                    ...(index === args.sourceList.length - 1 ? TABLE_COL_LAST : {}),
                  }}
                >
                  {column === 'obs_collection' ? (
                    <span
                      className="link"
                      onClick={() => handleCollectionClick(item[column])}
                    >
                      {item[column]}
                    </span>
                  ) : (
                    <div className="text-colour">{item[column]}</div>
                  )}
                </td>
              ))}
            </tr>
          ))}
        </>
      );
    } catch (e) {
      console.error('Error rendering table rows:', e);
      return <></>;
    }
  } // TableRows

  //	--------------------------------------------------------------------------
  //
  //	H O O K S
  //
  //	--------------------------------------------------------------------------

  //	------------------------------------------------------------
  //
  //	re-check if all rows are selected if the list of selected
  //	rows changes.
  //
  //	------------------------------------------------------------

  useEffect(() => {
    //
    console.log('[sSelectedRows] hook fires [search-results-table]');
    const allSelected: boolean = checkSelectAll({
      sourceList: sTapData,
      selectedRows: sSelectedRows,
      columns: sColumns,
    });
    dispatch(setSelectAll({ id: props.tabID, selectAll: allSelected }));
    //
  }, [sSelectedRows]);

  //	--------------------------------------------------------------------------
  //
  //	C O M P O N E N T   C O D E
  //
  //	--------------------------------------------------------------------------

  /* render a table row with this source */
  return (
    <div className="results-scroll">
      <Table
        bordered
        className="results-table"
        style={{ display: sLoadingTapData ? 'none' : 'table' }}
      >
        <thead>
          <tr className="search-results-table-row-header">
            <th style={{ ...TABLE_COL, ...TABLE_COL_HEADER }}>
              <button
                name="select-all-rows"
                type="button"
                title="Select/deselect all"
                className="action-button"
                onClick={checkChange}
              >
                <img
                  src={sSelectAll === true ? checkboxTicked : checkboxUnticked}
                  role="decoration"
                  alt=""
                  width="20"
                  style={{ marginTop: '10px' }}
                />
              </button>
            </th>
            <th style={{ ...TABLE_COL, ...TABLE_COL_HEADER }}>Actions</th>
            {TableHeadings()}
          </tr>
        </thead>
        <tbody>
          {TableRows({
            sourceList: sTapData,
            startIndex: sPage * sPageSize,
            onChange: rowCheckChange,
            selectedRows: sSelectedRows,
          })}
        </tbody>
      </Table>
      <div
        className={
          sLoadingTapData === true
            ? 'search-results-table-loading'
            : 'search-results-table-loaded'
        }
      >
        <img
          className="animated-gears"
          src={gearsIcon}
          role="decoration"
          alt=""
          width="60"
          height="60"
        />
        Loading
      </div>
      <div
        className="text-colour"
        style={{
          display: sErrorMessage ? 'flex' : 'none',
          flexDirection: 'row',
          alignItems: 'center',
          margin: '10px',
          padding: '10px',
          backgroundColor: 'rgba(255, 0, 0, 0.1)',
          border: '1px solid rgba(255, 0, 0, 0.3)',
          borderRadius: '4px',
        }}
      >
        <div
          style={{
            marginRight: '10px',
            fontSize: '20px',
            flex: '0 0 auto',
          }}
        >
          ⚠️
        </div>
        <div
          style={{
            flex: '1 1',
          }}
        >
          {sErrorMessage}
        </div>
      </div>
    </div>
  );
} // SearchResultsTable
